import {
  Box,
  Button,
  Checkbox,
  Chip,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router';
import { canManage, useSession } from '../auth/session';
import { AssetLabelsDialog } from '../components/LabelsDialog';
import { Empty, Loading, LoadError, Page } from '../components/Page';

import { retireStage } from '../components/AssetRegister';
import { AssetDialog, AssetStateChip } from '../components/asset/AssetDialog';
import { AssetThumb } from '../components/asset/AssetThumb';
import { OpenTransferChip } from '../components/StateChip';
import {
  ASSET_PAGE_SIZES,
  type AssetOverview,
  RETIRED_STATES,
  useAssetCounts,
  useAssets,
  useCategories,
  useSites,
} from '../data/queries';
import { useFirstPhotoUrls } from '../data/photos';
import { useLiveInvalidate } from '../data/realtime';

import { NewTransferButton } from '../components/NewTransferDialog';
import { idFilter, NONE, withParam } from '../lib/assetFilters';
import { useCertRules } from '../data/certificates';

const CERT_FILTERS = ['expired', 'expiring', 'missing', 'valid'] as const;

import { localeTag, relativeTime } from '../lib/time';

const STATE_FILTERS = [
  { value: '', label: 'common.all' },
  { value: 'on_site', label: 'state.on_site' },
  { value: 'in_transit,awaiting_receipt', label: 'state.in_transit' },
  { value: 'missing', label: 'state.missing' },
  { value: 'off_site', label: 'state.off_site' },
  { value: 'untagged', label: 'state.untagged' },
  // removed from service (CR_20260920_Asset_Register): hidden from every other chip, shown only here
  { value: 'awaiting_tag_removal', label: 'retire.filter_awaiting' },
  { value: 'retired', label: 'retire.filter_retired' },
];
const FILTER_STATES = [
  'on_site',
  'in_transit',
  'awaiting_receipt',
  'missing',
  'off_site',
  'unknown',
  'untagged',
  ...RETIRED_STATES,
];

function useDebounced<T>(value: T, ms: number): T {
  const [current, setCurrent] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setCurrent(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return current;
}

// states each chip stands for ([] = all)
const CHIP_STATES = STATE_FILTERS.map((f) => (f.value ? f.value.split(',') : []));

// Rows per page, remembered per browser; storage can be missing or refuse (private mode) — then the default.
const PAGE_SIZE_KEY = 'fsa.assets.page_size';
function readPageSize(): number {
  try {
    const n = Number(localStorage.getItem(PAGE_SIZE_KEY));
    return (ASSET_PAGE_SIZES as readonly number[]).includes(n) ? n : ASSET_PAGE_SIZES[0];
  } catch {
    return ASSET_PAGE_SIZES[0];
  }
}
function writePageSize(n: number) {
  try {
    localStorage.setItem(PAGE_SIZE_KEY, String(n));
  } catch {
    // not remembered; the list still works
  }
}

export function AssetsPage() {
  const { t, i18n } = useTranslation();
  const { me } = useSession();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  // ticked for label printing; kept across filter changes, so one batch can span several searches
  const [selected, setSelected] = useState<Map<string, AssetOverview>>(new Map());
  const [printing, setPrinting] = useState(false);
  // ?state=missing or ?state=in_transit,awaiting_receipt (the dashboard tiles open these) · ?site= · ?category=
  const [params, setParams] = useSearchParams();
  const stateParam = params.get('state') ?? '';
  const states = stateParam ? stateParam.split(',').filter((s) => FILTER_STATES.includes(s)) : [];
  const site = idFilter(params.get('site'));
  const category = idFilter(params.get('category'));
  // ?cert=expiring|expired|missing|valid (CR_20260921 §7.3) — shown once the account requires any certificate
  const certParam = params.get('cert') ?? '';
  const certState = (CERT_FILTERS as readonly string[]).includes(certParam) ? certParam : '';
  const rules = useCertRules();
  const anyRequired = !!rules.data?.length;
  const outOfService = params.get('service') === 'out';
  // ?registered=7 — registered in the last 7 days; ?sort=registered — newest first (CR_20260920_Asset_Register §9).
  // The cut-off is fixed while the page is open, so the query key does not change on every render.
  const recent = params.get('registered') === '7';
  const newestFirst = params.get('sort') === 'registered';
  const registeredSince = useMemo(
    () => (recent ? new Date(Date.now() - 7 * 86_400_000).toISOString() : undefined),
    [recent],
  );
  const filters = {
    search: useDebounced(search.trim(), 250),
    site,
    category,
    registeredSince,
    newestFirst,
    certState,
    outOfService,
  };
  // ?page= (1-based) keeps the page when coming back from an asset; any filter change goes back to the first page
  const [pageSize, setPageSize] = useState(readPageSize);
  const page = Math.max(0, (Number.parseInt(params.get('page') ?? '', 10) || 1) - 1);
  const assets = useAssets(filters, states, { page, size: pageSize });
  const thumbs = useFirstPhotoUrls(
    useMemo(() => (assets.data ?? []).map((a) => a.asset_id), [assets.data]),
  );
  const counts = useAssetCounts(filters, CHIP_STATES, states);
  const sites = useSites();
  const categories = useCategories();
  useLiveInvalidate('assets', ['asset_presence'], [['assets']]);

  const setFilter = (key: string, value: string) =>
    setParams(withParam(withParam(params, 'page', ''), key, value));
  const setPage = (p: number) => setParams(withParam(params, 'page', p > 0 ? String(p + 1) : ''));
  const lastSearch = useRef(filters.search);
  useEffect(() => {
    if (lastSearch.current === filters.search) return;
    lastSearch.current = filters.search;
    if (params.has('page')) setParams(withParam(params, 'page', ''), { replace: true });
  }, [filters.search, params, setParams]);
  const num = (n: number) => new Intl.NumberFormat(localeTag(i18n.language)).format(n);
  const chipIndex = STATE_FILTERS.findIndex((f) => f.value === stateParam);
  const shown =
    counts.data?.current ?? (chipIndex >= 0 ? counts.data?.byChip[chipIndex] : undefined);
  // a page past the end (assets removed meanwhile, an old link) shows the last one instead
  const lastPage = shown !== undefined ? Math.max(0, Math.ceil(shown / pageSize) - 1) : undefined;
  useEffect(() => {
    if (lastPage !== undefined && page > lastPage) setPage(lastPage);
  });
  const total = counts.data?.total;
  const filtered = !!(
    filters.search ||
    stateParam ||
    site ||
    category ||
    recent ||
    certState ||
    outOfService
  );
  // Location column: the lorry while in transit, else the site it is at (or was last at when missing); a retired
  // asset is no longer anywhere
  // where it was last seen (a sighting, or a QR-only asset's latest count); only its custody known = "not seen yet"
  const where = (a: AssetOverview) =>
    retireStage(a) !== 'active'
      ? null
      : a.display_state === 'in_transit'
        ? a.vehicle_ref
        : (a.seen_site_name ??
          a.presence_site_name ??
          (a.custody_site_name ? t('assets.where_not_seen', { site: a.custody_site_name }) : null));
  // label stays up with "All sites" / "All categories" showing
  const selectProps = { select: { displayEmpty: true }, inputLabel: { shrink: true } };
  const allTicked = !!assets.data?.length && assets.data.every((a) => selected.has(a.asset_id));
  const tick = (rows: AssetOverview[], on: boolean) =>
    setSelected((prev) => {
      const next = new Map(prev);
      for (const a of rows) {
        if (on) next.set(a.asset_id, a);
        else next.delete(a.asset_id);
      }
      return next;
    });

  return (
    <Page
      title={t('assets.title')}
      actions={
        <Stack direction="row" spacing={1}>
          {/* From pre-filled when the Site filter is one place the viewer can dispatch from (R0) */}
          <NewTransferButton initialFrom={site && site !== NONE ? site : undefined} />
          {canManage(me) ? (
            <Button variant="outlined" component={RouterLink} to="/assets/import">
              {t('import.title')}
            </Button>
          ) : null}
          <Button variant="contained" onClick={() => setCreating(true)}>
            {t('assets.new')}
          </Button>
        </Stack>
      }
    >
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          label={t('assets.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 280, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
        />
        <TextField
          select
          size="small"
          label={t('assets.filter_site')}
          value={site}
          onChange={(e) => setFilter('site', e.target.value)}
          slotProps={selectProps}
          sx={{ minWidth: 200, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
        >
          <MenuItem value="">{t('assets.all_sites')}</MenuItem>
          <MenuItem value={NONE}>{t('assets.no_site')}</MenuItem>
          {/* inactive sites stay findable: assets may still belong to one */}
          {(sites.data ?? []).map((s) => (
            <MenuItem key={s.site_id} value={s.site_id}>
              {s.is_active ? s.name : `${s.name} (${t('sites.inactive')})`}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('assets.category')}
          value={category}
          onChange={(e) => setFilter('category', e.target.value)}
          slotProps={selectProps}
          sx={{ minWidth: 200, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
        >
          <MenuItem value="">{t('assets.all_categories')}</MenuItem>
          <MenuItem value={NONE}>{t('assets.no_category')}</MenuItem>
          {(categories.data ?? []).map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
        {anyRequired || certState ? (
          <TextField
            select
            size="small"
            label={t('certs.filter')}
            value={certState}
            onChange={(e) => setFilter('cert', e.target.value)}
            slotProps={selectProps}
            sx={{ minWidth: 160, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
            data-testid="cert-filter"
          >
            <MenuItem value="">{t('certs.filter_any')}</MenuItem>
            {CERT_FILTERS.map((s) => (
              <MenuItem key={s} value={s}>
                {t(`certs.state_${s}`)}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        sx={{ mb: 2 }}
        flexWrap="wrap"
        useFlexGap
        alignItems="center"
      >
        {STATE_FILTERS.map((f, i) => {
          const n = counts.data?.byChip[i];
          return (
            <Chip
              key={f.value}
              label={n === undefined ? t(f.label) : `${t(f.label)} (${num(n)})`}
              color={stateParam === f.value ? 'primary' : 'default'}
              variant={stateParam === f.value ? 'filled' : 'outlined'}
              onClick={() => setFilter('state', f.value)}
            />
          );
        })}
        <Chip
          label={t('assets.registered_recent')}
          color={recent ? 'primary' : 'default'}
          variant={recent ? 'filled' : 'outlined'}
          onClick={() => setFilter('registered', recent ? '' : '7')}
          data-testid="registered-recent"
        />
        <Chip
          label={t('service.filter')}
          color={outOfService ? 'primary' : 'default'}
          variant={outOfService ? 'filled' : 'outlined'}
          onClick={() => setFilter('service', outOfService ? '' : 'out')}
          data-testid="filter-out-of-service"
        />
        {total !== undefined && shown !== undefined ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ml: 'auto', fontVariantNumeric: 'tabular-nums' }}
          >
            {filtered
              ? t('assets.count_filtered', { shown: num(shown), total: num(total) })
              : t('assets.count', { count: total, n: num(total) })}
          </Typography>
        ) : null}
      </Stack>
      {selected.size > 0 ? (
        <Paper
          variant="outlined"
          sx={{ px: 1.5, py: 1, mb: 1 }}
          data-testid="label-bar"
          role="region"
          aria-label={t('labels.print_selected')}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {t('board.selected', { count: selected.size })}
            </Typography>
            <Button size="small" variant="contained" onClick={() => setPrinting(true)}>
              {t('labels.print_selected')}
            </Button>
            <Button size="small" onClick={() => setSelected(new Map())}>
              {t('labels.clear_selection')}
            </Button>
          </Stack>
        </Paper>
      ) : null}
      {assets.isLoading ? (
        <Loading />
      ) : assets.error ? (
        <LoadError error={assets.error} />
      ) : !assets.data?.length ? (
        <Empty>{t('assets.empty')}</Empty>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allTicked}
                    onChange={(e) => tick(assets.data ?? [], e.target.checked)}
                    slotProps={{ input: { 'aria-label': t('labels.select_all') } }}
                  />
                </TableCell>
                <TableCell>{t('assets.code')}</TableCell>
                <TableCell>{t('assets.category')}</TableCell>
                <TableCell>{t('assets.col_location')}</TableCell>
                <TableCell>{t('assets.col_status')}</TableCell>
                <TableCell>{t('assets.last_seen')}</TableCell>
                <TableCell sortDirection={newestFirst ? 'desc' : false}>
                  <TableSortLabel
                    active={newestFirst}
                    direction="desc"
                    onClick={() => setFilter('sort', newestFirst ? '' : 'registered')}
                  >
                    {t('assets.registered')}
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assets.data.map((a) => (
                <TableRow
                  key={a.asset_id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/assets/${a.asset_id}`)}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selected.has(a.asset_id)}
                      onChange={(e) => tick([a], e.target.checked)}
                      slotProps={{
                        input: { 'aria-label': t('board.select_row', { asset: a.asset_code }) },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                      <AssetThumb url={thumbs.data?.get(a.asset_id)} />
                      <Box>
                        {a.asset_code}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {a.description}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>{a.category_name ?? '—'}</TableCell>
                  <TableCell>
                    {where(a) ?? '—'}
                    {a.zone_name ? (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {a.zone_name}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                      <AssetStateChip a={a} />
                      <OpenTransferChip a={a} />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {relativeTime(a.last_seen_at, i18n.language) ?? t('assets.not_seen')}
                  </TableCell>
                  <TableCell>
                    {relativeTime(a.created_at, i18n.language) ?? '—'}
                    {a.created_by_name ? (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {a.created_by_name}
                      </Typography>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {shown !== undefined && shown > ASSET_PAGE_SIZES[0] ? (
            <TablePagination
              component="div"
              count={shown}
              page={Math.min(page, lastPage ?? page)}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={pageSize}
              rowsPerPageOptions={[...ASSET_PAGE_SIZES]}
              onRowsPerPageChange={(e) => {
                const n = Number(e.target.value);
                setPageSize(n);
                writePageSize(n);
                setPage(0);
              }}
              labelRowsPerPage={t('assets.rows_per_page')}
              labelDisplayedRows={({ from, to, count }) =>
                t('assets.page_range', { from: num(from), to: num(to), count: num(count) })
              }
              getItemAriaLabel={(type) => t(`assets.page_${type}`)}
              data-testid="assets-pager"
            />
          ) : null}
        </Box>
      )}
      {creating && <AssetDialog open={creating} onClose={() => setCreating(false)} />}
      {printing ? (
        <AssetLabelsDialog assets={[...selected.values()]} onClose={() => setPrinting(false)} />
      ) : null}
    </Page>
  );
}
